import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";
import { MapPin, Loader2, Trophy, Bell, BellOff, PackageCheck } from "lucide-react";
import { playChime, primeChime, chimeEnabled, setChimeEnabled } from "@/lib/chime";
import AerialViewDialog from "@/components/AerialViewDialog";
import { FlowLayer, STAGE_COLORS, arcLatLngs } from "@/components/map/FlowLayer";
import { carrierLogoUrl } from "@/lib/carrierLogos";
import { addBasemap } from "@/lib/basemap";
import OpsHud from "@/components/map/OpsHud";
import TruckCard from "@/components/map/TruckCard";

const US_CENTER = [39.5, -98.35];
const RAINVIEWER_META = "https://api.rainviewer.com/public/weather-maps.json";
const DAY_MS = 86400000;
const LANE_FILTERS = [["active", "Active"], ["30d", "30 days"], ["90d", "90 days"], ["all", "All"]];

function withinDays(iso, days) {
  const t = Date.parse(iso || "");
  return isFinite(t) && Date.now() - t <= days * DAY_MS;
}

function filterRoutes(all, f) {
  if (f === "all") return all;
  return all.filter((r) => {
    if (r.stage !== "delivered") return true;
    if (f === "active") return false;
    return withinDays(r.delivered_at, f === "30d" ? 30 : 90);
  });
}

const destIcon = (stage, atRisk) => {
  const color = STAGE_COLORS[stage] || STAGE_COLORS.won;
  const check = stage === "delivered"
    ? `<div style="position:absolute;top:-6px;right:-6px;width:13px;height:13px;border-radius:9999px;background:#10b981;border:1.5px solid #fff;display:flex;align-items:center;justify-content:center"><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg></div>`
    : "";
  const ring = atRisk
    ? `<div style="position:absolute;left:50%;top:50%;width:28px;height:28px;margin-left:-14px;margin-top:-14px;border-radius:9999px;border:2px solid #ef4444;box-shadow:0 0 8px #ef4444cc"></div>`
    : "";
  return L.divIcon({
    className: "",
    html: `<div style="position:relative;width:18px;height:18px">
      ${ring}<div style="width:18px;height:18px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.5)"></div>${check}
    </div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 18],
  });
};

const truckSvg = (color) =>
  `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>`;

const truckIcon = (stage, carrier) => {
  const color = STAGE_COLORS[stage] || STAGE_COLORS.transit;
  const logo = carrierLogoUrl(carrier);
  const inner = logo
    ? `<img src="${logo}" alt="" style="width:18px;height:18px;object-fit:contain;background:#fff;border-radius:4px;padding:1px" onerror="this.style.display='none';this.parentNode.querySelector('.truck-fallback').style.display='flex'"/>
       <span class="truck-fallback" style="display:none;align-items:center;justify-content:center">${truckSvg(color)}</span>`
    : truckSvg(color);
  return L.divIcon({
    className: "",
    html: `<div style="width:26px;height:26px;border-radius:9999px;background:#0b0f14;border:2px solid ${color};box-shadow:0 0 10px ${color}99,0 2px 6px rgba(0,0,0,.6);display:flex;align-items:center;justify-content:center">
      ${inner}
    </div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
};

const progressFor = (rt) => {
  if (rt.stage === "delivered") return 1;
  const start = Date.parse(rt.pickup_date || rt.booked_at || "");
  const end = Date.parse(rt.estimated_delivery || "");
  if (isFinite(start) && isFinite(end) && end > start) {
    return Math.min(0.94, Math.max(0.06, (Date.now() - start) / (end - start)));
  }
  return 0.35;
};

export default function LiveMap() {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const flowRef = useRef(null);
  const radarRef = useRef({ layers: [], timer: null });
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [truck, setTruck] = useState(null);
  const [radarOn, setRadarOn] = useState(false);
  const [cinematic, setCinematic] = useState(false);
  const [laneFilter, setLaneFilter] = useState("active");
  const [soundOn, setSoundOn] = useState(chimeEnabled);
  const prevDelivered = useRef(null);
  const armed = useRef(false);
  const fitKey = useRef(null);
  const visibleRoutes = useMemo(() => filterRoutes(data?.routes || [], laneFilter), [data, laneFilter]);

  const isToday = (val) => {
    const d = new Date(val || "");
    if (isNaN(d)) return false;
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  };
  const deliveredTodayCount = useMemo(
    () => (data?.routes || []).filter((r) => r.stage === "delivered" && isToday(r.delivered_at)).length,
    [data],
  );

  const pingDelivery = (rt) => {
    const map = mapRef.current;
    if (!map || !rt?.destination) return;
    const ring = L.circleMarker([rt.destination.lat, rt.destination.lng], {
      radius: 6, color: "#10b981", weight: 2, fillColor: "#10b981", fillOpacity: 0.3, interactive: false,
    }).addTo(map);
    let r = 6, op = 0.9;
    const iv = setInterval(() => {
      r += 4; op -= 0.06;
      ring.setRadius(r);
      ring.setStyle({ opacity: Math.max(0, op), fillOpacity: Math.max(0, op * 0.3) });
      if (op <= 0) { clearInterval(iv); if (map.hasLayer(ring)) map.removeLayer(ring); }
    }, 55);
  };

  const detectDeliveries = (routeList) => {
    const current = new Set(routeList.filter((r) => r.stage === "delivered").map((r) => r.id));
    if (!armed.current) { prevDelivered.current = current; return; }
    const fresh = routeList.filter((r) => r.stage === "delivered" && !prevDelivered.current.has(r.id));
    prevDelivered.current = current;
    if (fresh.length === 0) return;
    if (chimeEnabled()) playChime();
    fresh.slice(0, 4).forEach(pingDelivery);
    if (fresh.length === 1) {
      const f = fresh[0];
      toast.success(`Delivered — ${f.quote_number}`, { description: `${f.customer} · ${f.origin?.state || ""} → ${f.destination?.state || ""}` });
    } else {
      toast.success(`${fresh.length} shipments delivered`, { description: fresh.map((f) => f.quote_number).join(", ") });
    }
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setChimeEnabled(next);
    if (next) { primeChime(); playChime(); }
  };

  useEffect(() => {
    let alive = true;
    const fetchRoutes = (silent) =>
      api.get("/map/won-routes")
        .then((r) => { if (!alive) return; setData(r.data); detectDeliveries(r.data.routes || []); })
        .catch((e) => { if (alive && !silent) toast.error(apiError(e)); })
        .finally(() => { if (alive) setLoading(false); });
    fetchRoutes(false).finally(() => { armed.current = true; });
    const iv = setInterval(() => fetchRoutes(true), 120000);
    const unlock = () => primeChime();
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => { alive = false; clearInterval(iv); window.removeEventListener("pointerdown", unlock); };
  }, []);

  // Weather risks for active lanes (Open-Meteo, cached server-side)
  useEffect(() => {
    const routes = (data?.routes || []).filter((r) => r.stage !== "delivered");
    if (!routes.length) return;
    api
      .post("/map/weather-risks", {
        lanes: routes.map((r) => ({
          id: r.id, quote_number: r.quote_number,
          olat: r.origin.lat, olng: r.origin.lng, dlat: r.destination.lat, dlng: r.destination.lng,
          pickup_date: r.pickup_date || null, estimated_delivery: r.estimated_delivery || null,
        })),
      })
      .then((r) => setRisks(r.data.risks || []))
      .catch(() => {});
  }, [data]);

  useEffect(() => {
    if (!data || !mapEl.current) return;
    const dark = document.documentElement.classList.contains("dark");
    if (!mapRef.current) {
      mapRef.current = L.map(mapEl.current, { zoomControl: true, scrollWheelZoom: true, minZoom: 3, maxZoom: 10, worldCopyJump: true });
      addBasemap(mapRef.current, { dark });
      mapRef.current.setView(US_CENTER, 4);
    }
    const map = mapRef.current;
    const routes = visibleRoutes;
    const layer = L.layerGroup().addTo(map);
    const clusters = L.markerClusterGroup({
      maxClusterRadius: 42,
      disableClusteringAtZoom: 8,
      showCoverageOnHover: false,
      iconCreateFunction: (cluster) => {
        const stages = cluster.getAllChildMarkers().map((m) => m.options.stage);
        const color = stages.includes("transit") ? STAGE_COLORS.transit
          : stages.includes("booked") ? STAGE_COLORS.booked
          : stages.includes("delivered") ? STAGE_COLORS.delivered : STAGE_COLORS.won;
        return L.divIcon({
          className: "",
          html: `<div style="width:34px;height:34px;border-radius:9999px;background:rgba(11,15,20,.92);border:2px solid ${color};box-shadow:0 0 12px ${color}88;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;font-size:13px">${cluster.getChildCount()}</div>`,
          iconSize: [34, 34], iconAnchor: [17, 17],
        });
      },
    }).addTo(map);

    flowRef.current = new FlowLayer(map, routes);

    routes.forEach((rt) => {
      const sameSpot = Math.abs(rt.origin.lat - rt.destination.lat) < 1e-4 && Math.abs(rt.origin.lng - rt.destination.lng) < 1e-4;
      const destPos = sameSpot ? [rt.destination.lat + 0.045, rt.destination.lng + 0.045] : [rt.destination.lat, rt.destination.lng];
      const bolLine = rt.has_bol ? `<br/><span style="color:#10b981;font-weight:700">&#10003; ${rt.bol_type === "GlobalTranz" ? "BOL" : "PRO"} ${rt.bol_number || ""}${rt.bol_type ? ` &middot; ${rt.bol_type}` : ""}</span>` : "";
      const lateLine = rt.at_risk ? `<br/><span style="color:#ef4444;font-weight:800">&#9888; LATE &middot; ${rt.risk_reason || "ETA passed"}</span>` : "";
      const tip = `<b>${rt.customer}</b><br/>${rt.origin.city}, ${rt.origin.state} &rarr; ${rt.destination.city}, ${rt.destination.state}<br/>${money(rt.value)} &middot; ${(rt.stage || "").toUpperCase()}${bolLine}${lateLine}<br/><span style="opacity:.65">Click for details</span>`;
      L.marker(destPos, { icon: destIcon(rt.stage, rt.at_risk), stage: rt.stage })
        .addTo(clusters)
        .bindTooltip(tip, { direction: "top", offset: [0, -10] })
        .on("click", () => setTruck(rt));

      // Live truck marker riding the arc for in-transit loads
      if (rt.stage === "transit" && !sameSpot) {
        const pts = arcLatLngs(rt.origin, rt.destination);
        const idx = Math.round(progressFor(rt) * (pts.length - 1));
        const ll = pts[Math.min(idx, pts.length - 1)];
        L.marker(ll, { icon: truckIcon(rt.stage, rt.carrier), zIndexOffset: 500 })
          .addTo(layer)
          .bindTooltip(`<b>${rt.quote_number}</b> · ${rt.carrier || "In transit"}<br/><span style="opacity:.7">${Math.round(progressFor(rt) * 100)}% of transit window</span>`, { direction: "top", offset: [0, -14] })
          .on("click", () => setTruck(rt));
      }
    });

    if (routes.length && fitKey.current !== laneFilter) {
      const bounds = [];
      routes.forEach((rt) => { bounds.push([rt.origin.lat, rt.origin.lng], [rt.destination.lat, rt.destination.lng]); });
      try { map.fitBounds(bounds, { padding: [60, 60], maxZoom: 6 }); } catch (_) { /* ignore */ }
      fitKey.current = laneFilter;
    }
    const t0 = setTimeout(() => map.invalidateSize(), 150);

    return () => {
      clearTimeout(t0);
      map.removeLayer(layer);
      map.removeLayer(clusters);
      if (flowRef.current) { flowRef.current.destroy(); flowRef.current = null; }
    };
  }, [data, visibleRoutes, laneFilter]);

  // Animated weather radar (RainViewer — free) cycling the last 6 frames
  const toggleRadar = useCallback(async () => {
    const map = mapRef.current;
    if (!map) return;
    const st = radarRef.current;
    if (radarOn) {
      clearInterval(st.timer);
      st.layers.forEach((l) => map.removeLayer(l));
      st.layers = [];
      setRadarOn(false);
      return;
    }
    try {
      const res = await fetch(RAINVIEWER_META);
      const j = await res.json();
      const frames = (j?.radar?.past || []).slice(-6);
      if (!frames.length) { toast.error("Radar data unavailable right now."); return; }
      st.layers = frames.map((f) =>
        L.tileLayer(`${j.host}${f.path}/256/{z}/{x}/{y}/2/1_1.png`, { opacity: 0, zIndex: 350 }).addTo(map));
      let idx = st.layers.length - 1;
      st.layers[idx].setOpacity(0.62);
      st.timer = setInterval(() => {
        st.layers[idx].setOpacity(0);
        idx = (idx + 1) % st.layers.length;
        st.layers[idx].setOpacity(0.62);
      }, 800);
      setRadarOn(true);
    } catch (_) {
      toast.error("Radar data unavailable right now.");
    }
  }, [radarOn]);

  useEffect(() => () => { const st = radarRef.current; clearInterval(st.timer); }, []);

  const focusRoute = useCallback((rt) => {
    const map = mapRef.current;
    if (!map || !rt) return;
    const mid = [(rt.origin.lat + rt.destination.lat) / 2, (rt.origin.lng + rt.destination.lng) / 2];
    map.flyTo(mid, Math.max(map.getZoom(), 5.5), { duration: 1.6 });
    setTruck(rt);
  }, []);

  // Cinematic "Ops Wall" mode — auto-fly between active shipments
  useEffect(() => {
    const map = mapRef.current;
    if (!cinematic || !map || !data) return;
    const routes = visibleRoutes;
    const actives = routes.filter((r) => r.stage === "transit" || r.stage === "booked");
    const list = actives.length ? actives : routes;
    if (!list.length) return;
    let i = 0;
    const tick = () => {
      const rt = list[i % list.length];
      i += 1;
      const mid = [(rt.origin.lat + rt.destination.lat) / 2, (rt.origin.lng + rt.destination.lng) / 2];
      map.flyTo(mid, 6, { duration: 2.4 });
      setTruck(rt);
    };
    tick();
    const iv = setInterval(tick, 7500);
    const stop = () => setCinematic(false);
    map.on("dragstart", stop);
    return () => { clearInterval(iv); map.off("dragstart", stop); };
  }, [cinematic, visibleRoutes]);

  useEffect(
    () => () => {
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    },
    [],
  );

  const counts = data?.counts || {};
  const routes = visibleRoutes;
  const routeCount = routes.length;
  const riskById = Object.fromEntries((risks || []).map((r) => [r.id, r]));
  const chips = [
    { group: "Quotes", items: [["Draft", counts.draft || 0], ["Quoted", (counts.quoted || 0) + (counts.sent || 0)]] },
    { group: "Won", items: [["Won", counts.won || 0], ["Booked", counts.booked || 0]] },
  ];

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col gap-3 animate-fade-up" data-testid="live-map-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-black tracking-tighter">Live Operations Map</h1>
          <p className="text-sm text-muted-foreground">Live lanes, trucks &amp; weather across North America</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-lg border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 px-3 py-1.5 text-xs font-semibold text-[hsl(var(--success))]"
            data-testid="map-delivered-today" title="Shipments delivered today">
            <PackageCheck className="h-3.5 w-3.5" />
            {deliveredTodayCount} delivered today
          </div>
          <button onClick={toggleSound} data-testid="map-chime-toggle" title={soundOn ? "Mute delivery chime" : "Unmute delivery chime"}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition hover:text-foreground">
            {soundOn ? <Bell className="h-4 w-4 text-[hsl(var(--success))]" /> : <BellOff className="h-4 w-4" />}
          </button>
          <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-1" data-testid="map-lane-filter">
            {LANE_FILTERS.map(([val, lbl]) => (
              <button key={val} onClick={() => setLaneFilter(val)} data-testid={`map-filter-${val}`}
                className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${laneFilter === val ? "bg-[hsl(var(--primary))] text-white" : "text-muted-foreground hover:bg-secondary"}`}>
                {lbl}
              </button>
            ))}
          </div>
          {chips.map((c) => (
            <div key={c.group} className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-1.5" data-testid={`chip-${c.group.toLowerCase()}`}>
              <span className="label-caps">{c.group}</span>
              {c.items.map(([l, v]) => (
                <span key={l} className="flex items-baseline gap-1 text-sm">
                  <span className="metric-num font-bold">{v}</span>
                  <span className="text-xs text-muted-foreground">{l}</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="relative flex-1 overflow-hidden rounded-lg border border-border bg-card">
        <div ref={mapEl} className="absolute inset-0 z-0" data-testid="live-map-canvas" />
        {loading && (
          <div className="absolute inset-0 z-[500] flex items-center justify-center bg-card/70">
            <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" />
          </div>
        )}
        {!loading && routeCount === 0 && (
          <div className="absolute inset-0 z-[500] flex items-center justify-center p-6 text-center text-sm text-muted-foreground" data-testid="live-map-empty">
            <div>
              <MapPin className="mx-auto mb-2 h-6 w-6" />
              No won deals yet — mark a quote as <span className="font-bold text-foreground">Won</span> and its lane will appear here.
            </div>
          </div>
        )}

        {!loading && routeCount > 0 && (
          <OpsHud
            routes={routes}
            risks={risks}
            radarOn={radarOn}
            onToggleRadar={toggleRadar}
            cinematic={cinematic}
            onToggleCinematic={() => setCinematic((v) => !v)}
            onFocus={focusRoute}
            totalWon={data?.total_won_value}
          />
        )}

        <TruckCard
          rt={truck}
          risk={truck ? riskById[truck.id] : null}
          onClose={() => setTruck(null)}
          onOpenQuote={(id) => { setTruck(null); if (id) navigate(`/quotes/${id}`); }}
          onAerial={(rt) => { setTruck(null); setSelected(rt); }}
        />

        <div className="pointer-events-none absolute bottom-3 left-3 z-[600] flex items-center gap-3 rounded-full bg-zinc-900/85 px-4 py-2 text-white shadow-lg backdrop-blur" data-testid="live-map-summary">
          <span className="flex items-center gap-1.5 text-sm font-bold"><MapPin className="h-4 w-4 text-[hsl(var(--primary))]" />{routeCount} {routeCount === 1 ? "lane" : "lanes"}</span>
          <span className="flex items-center gap-3 border-l border-white/15 pl-3 text-[10px] font-bold uppercase tracking-wide" data-testid="map-legend">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: STAGE_COLORS.booked }} />Booked</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: STAGE_COLORS.transit }} />Transit</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: STAGE_COLORS.delivered }} />Delivered</span>
          </span>
          {data?.late_count > 0 && (
            <span className="flex items-center gap-1 border-l border-white/15 pl-3 text-xs font-extrabold text-red-400" data-testid="map-late-count">
              ⚠ {data.late_count} late
            </span>
          )}
        </div>
        {routeCount > 0 && (
          <div className="absolute bottom-3 right-3 z-[600] flex items-center gap-2 rounded-full bg-[hsl(var(--success))]/90 px-4 py-2 text-white shadow-lg" data-testid="live-map-value">
            <Trophy className="h-4 w-4" />
            <span className="text-sm font-bold">{money(data?.total_won_value || 0)} won</span>
          </div>
        )}
      </div>

      <AerialViewDialog
        route={selected}
        onClose={() => setSelected(null)}
        onOpenQuote={(id) => { setSelected(null); if (id) navigate(`/quotes/${id}`); }}
      />
    </div>
  );
}
